import { useDebounce } from "hooks/useDebounce"
import { type ChangeEvent, useEffect, useRef, useState } from "react"
import { Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { rgbToHex, tintRgbTohex } from "./helpers"

import chroma from "chroma-js"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { HexColorPicker } from "react-colorful"

export { tintRgbTohex, rgbToHex }

interface ColorItemProps {
  value: string
  onChange: (value: string) => void
  onDelete?: () => void
  disabledDelete?: boolean
}

export const ColorItem = ({ value, onChange, onDelete }: ColorItemProps) => {
  const [color, setColor] = useState<string>(value)
  const debouncedValue = useDebounce(color, 200)
  // Tracks the last value that crossed the component boundary (either pushed up via
  // onChange or adopted down from the prop), so external resets (e.g. Discard) are
  // adopted instead of being overwritten by a stale debounced echo.
  const lastSyncedValueRef = useRef(value)

  useEffect(() => {
    if (value !== lastSyncedValueRef.current) {
      lastSyncedValueRef.current = value
      setColor(value)
    }
  }, [value])

  useEffect(() => {
    if (debouncedValue !== lastSyncedValueRef.current) {
      lastSyncedValueRef.current = debouncedValue
      onChange(debouncedValue)
    }
  }, [debouncedValue, onChange])

  const c = chroma.valid(color) ? chroma(color) : undefined
  const [popoverActive, setPopoverActive] = useState(false)
  const [colorHex, setColorHex] = useState<string>(c ? c.hex("rgb") : "")

  const [rgb, setRgb] = useState(() => {
    if (!c) return { r: "", g: "", b: "" }
    const [r, g, b] = c.rgb()
    return { r: r.toString(), g: g.toString(), b: b.toString() }
  })

  useEffect(() => {
    setColorHex(color)
    if (chroma.valid(color)) {
      const [r, g, b] = chroma(color).rgb()
      setRgb({ r: r.toString(), g: g.toString(), b: b.toString() })
    }
  }, [color])

  const handleHexChange = (v: string) => {
    if (v[0] !== "#") v = "#" + v
    if (v.length > 7) v = v.slice(0, 7)
    setColorHex(v)
    if (chroma.valid(v)) {
      const validChroma = chroma(v)
      const [r, g, b] = validChroma.rgb()
      setRgb({ r: r.toString(), g: g.toString(), b: b.toString() })
      setColor(v)
    }
  }

  const handleRgbChange = (channel: "r" | "g" | "b", val: string) => {
    if (val.length > 3) val = val.slice(0, 3)
    const next = { ...rgb, [channel]: val }
    setRgb(next)
    const r = parseInt(next.r)
    const g = parseInt(next.g)
    const b = parseInt(next.b)
    if ([r, g, b].some((n) => isNaN(n) || n < 0 || n > 255)) {
      setColor("")
      setColorHex("")
      return
    }
    const hex = chroma.rgb(r, g, b).hex("rgb")
    setColorHex(hex)
    setColor(hex)
  }

  return (
    <div className="flex items-center gap-2">
      <Popover open={popoverActive} onOpenChange={setPopoverActive}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-9 w-9 shrink-0 cursor-pointer rounded-md border shadow-sm transition-shadow hover:shadow-md"
            style={{ backgroundColor: c?.css() || "transparent" }}
          >
            {!c?.css() && (
              <div className="flex h-full w-full items-center justify-center rounded-md">
                <div className="absolute h-0.5 w-6 rotate-45 bg-destructive" />
                <div className="absolute h-0.5 w-6 -rotate-45 bg-destructive" />
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-3">
            <HexColorPicker
              color={chroma.valid(color) ? color : "#FF0000"}
              onChange={setColor}
              style={{ width: "100%" }}
            />
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 shrink-0 rounded border"
                style={{ backgroundColor: c?.css() || "transparent" }}
              />
              <Input
                value={colorHex}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleHexChange(e.target.value)}
                placeholder="#FFFFFF"
                className="h-8 font-mono text-xs"
              />
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {(["r", "g", "b"] as const).map((channel) => (
                <div key={channel} className="space-y-1">
                  <Label className="text-[10px] uppercase text-muted-foreground">{channel}</Label>
                  <Input
                    value={rgb[channel]}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleRgbChange(channel, e.target.value)
                    }
                    placeholder="0-255"
                    className="h-7 text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
